import { create, tsx } from '@dojo/framework/core/vdom';
import { Outlet } from '@dojo/framework/routing/Outlet';
import Footer from './widgets/Footer';
import About from './pages/About';
import AgileConsulting from './pages/expertise/AgileConsulting';
import AutomatedTesting from './pages/services/AutomatedTesting';
import Careers from './pages/careers/Careers';
import Contact from './pages/Contact';
import ContactThankyou from './pages/ContactThankyou';
import Expertise from './pages/expertise/Expertise';
import Home from './pages/Home';
import OpenSource from './pages/open-source/OpenSource';
import Privacy from './pages/Privacy';
import Services from './pages/services/Services';
import Work from './pages/our-work/Work';
import CaseStudyFannie from './pages/our-work/CaseStudyFannie';
import CaseStudyMarriott from './pages/our-work/CaseStudyMarriott';
import CaseStudyBuilt from './pages/our-work/CaseStudyBuilt';
import CaseStudySocialRVComplete from './pages/our-work/SocialRVComplete';
import CaseStudySocialRVLife from './pages/our-work/SocialRVLife';
import CaseStudySocialTripWizard from './pages/our-work/SocialTripWizard';
import CaseStudyCentric from './pages/our-work/CaseStudyCentric';
import CrossPlatformDevelopment from './pages/services/CrossPlatformDevelopment';
import OpenSourceDojo from './pages/open-source/OpenSourceDojo';
import OpenSourceIntern from './pages/open-source/OpenSourceIntern';
import OpenSourceTypescript from './pages/open-source/OpenSourceTypescript';
import OpenSourceDojoToolkit from './pages/open-source/OpenSourceDojoToolkit';
import OpenSourceDgrid from './pages/open-source/OpenSourceDgrid';
import PerformanceTesting from './pages/services/PerformanceTesting';
import QualityAssurance from './pages/services/QualityAssurance';
import GDPRBanner from './widgets/GDPRBanner';
import Error404 from './pages/Error404';
import StrategySession from './pages/StrategySession';
import JavaScriptDevelopment from './pages/expertise/JavascriptDevelopment';
import FullStackDevelopment from './pages/expertise/FullStackDevelopment';
import CustomSoftwareDevelopment from './pages/services/CustomSoftwareDevelopment';
import DevOpsSolutions from './pages/services/DevOpsSolutions';
import DojoDevelopment from './pages/expertise/DojoDevelopment';
import EnterpriseArchitecture from './pages/services/EnterpriseArchitecture';
import EnterpriseSoftwareDevelopment from './pages/services/EnterpriseSoftwareDevelopment';
import EnterpriseCloudServices from './pages/services/EnterpriseCloudServices';
import EnterpriseTechnologyStrategy from './pages/services/EnterpriseTechnologyStrategy';
import EnterpriseUX from './pages/services/EnterpriseUX';
import { JobPost } from './widgets/JobPost';
import RapidPrototyping from './pages/services/RapidPrototyping';
import ReactDevelopment from './pages/expertise/ReactDevelopment';
import SoftwareArchitecture from './pages/services/SoftwareArchitecture';
import SoftwareModernization from './pages/services/SoftwareModernization';
import TypeScriptDevelopment from './pages/expertise/TypeScriptDevelopment';
import UXDesign from './pages/services/UXDesign';
import VueDevelopment from './pages/expertise/VueDevelopment';
import WebApplicationDevelopment from './pages/services/WebApplicationDevelopment';
import AngularDevelopment from './pages/expertise/AngularDevelopment';
import NodeDevelopment from './pages/expertise/NodeDevelopment';
import PerformanceOptimization from './pages/services/PerformanceOptimization';
import UIDesign from './pages/services/UIDesign';
import Navigation from './widgets/Navigation';
import { Blogs } from './pages/blog/Blogs';
import Blog from './pages/blog/Blog';
import BlogList from './pages/blog/BlogList';
import BlogSeries from './pages/blog/BlogSeries';
import CaseStudyTRowePrice from './pages/our-work/CaseStudyTRowePrice';
import CaseStudyIntuit from './pages/our-work/CaseStudyIntuit';
import NewsLetterSubmit from './pages/newsletter/Submit';
import NewsLetterForm from './pages/newsletter/Form';

import * as css from './App.m.css';

const appChromeFactory = create();
export const AppChrome = appChromeFactory(function AppChrome({ children }) {
	return (
		<virtual>
			<Navigation />
			<main>{children()}</main>
			<Footer />
			<GDPRBanner />
		</virtual>
	);
});

const factory = create();
export const App = factory(function App() {
	return (
		<div classes={css.root}>
			<Outlet id="iframe">
				{{
					previewLatestInsights: <BlogList type="latest" />,
					previewSeries: ({ params: { slug } }) => (
						<BlogList type="series" termOrSlug={slug} />
					),
					newsletterForm: <NewsLetterForm />,
					newsletterSubmit: ({ queryParams: { email, _gotcha } }) => (
						<NewsLetterSubmit email={email} _gotcha={_gotcha} />
					)
				}}
			</Outlet>
			<Outlet
				id="main"
				matcher={(defaultMatchers, matchDetailsMap) => {
					const blogMatch = matchDetailsMap.get('blog');
					const blogPageOrPostMatch = matchDetailsMap.get('blogPage');
					if (blogMatch) {
						defaultMatchers.blog = blogMatch.isExact();
					}

					if (blogPageOrPostMatch) {
						const isPage = !!Number(blogPageOrPostMatch.params.slugOrPage);
						defaultMatchers.blogPage = isPage;
						defaultMatchers.blogPost = !isPage;
					}

					return defaultMatchers;
				}}
			>
				{{
					about: (
						<AppChrome>
							<About />
						</AppChrome>
					),
					careerPage: ({ params: { jobId } }) => (
						<AppChrome>
							<JobPost id={jobId} />
						</AppChrome>
					),
					careers: ({ isExact }) => {
						return (
							isExact() && (
								<AppChrome>
									<Careers />
								</AppChrome>
							)
						);
					},
					contact: (
						<AppChrome>
							<Contact />
						</AppChrome>
					),
					strategySession: (
						<AppChrome>
							<StrategySession />
						</AppChrome>
					),
					contactThankyou: (
						<AppChrome>
							<ContactThankyou />
						</AppChrome>
					),
					expertise: ({ isExact }) => {
						return (
							isExact() && (
								<AppChrome>
									<Expertise />
								</AppChrome>
							)
						);
					},
					home: (
						<AppChrome>
							<Home />
						</AppChrome>
					),
					opensource: (
						<AppChrome>
							<OpenSource />
						</AppChrome>
					),
					opensourceDojo: (
						<AppChrome>
							<OpenSourceDojo />
						</AppChrome>
					),
					opensourceIntern: (
						<AppChrome>
							<OpenSourceIntern />
						</AppChrome>
					),
					opensourceTypescript: (
						<AppChrome>
							<OpenSourceTypescript />
						</AppChrome>
					),
					opensourceDojoToolkit: (
						<AppChrome>
							<OpenSourceDojoToolkit />
						</AppChrome>
					),
					opensourceDgrid: (
						<AppChrome>
							<OpenSourceDgrid />
						</AppChrome>
					),
					privacy: (
						<AppChrome>
							<Privacy />
						</AppChrome>
					),
					services: ({ isExact }) => {
						return (
							isExact() && (
								<AppChrome>
									<Services />
								</AppChrome>
							)
						);
					},
					work: (
						<AppChrome>
							<Work />
						</AppChrome>
					),
					casestudyFannie: (
						<AppChrome>
							<CaseStudyFannie />
						</AppChrome>
					),
					casestudyMarriott: (
						<AppChrome>
							<CaseStudyMarriott />
						</AppChrome>
					),
					casestudyBuilt: (
						<AppChrome>
							<CaseStudyBuilt />
						</AppChrome>
					),
					casestudySocialRVComplete: (
						<AppChrome>
							<CaseStudySocialRVComplete />
						</AppChrome>
					),
					casestudySocialRVLife: (
						<AppChrome>
							<CaseStudySocialRVLife />
						</AppChrome>
					),
					casestudySocialTripWizard: (
						<AppChrome>
							<CaseStudySocialTripWizard />
						</AppChrome>
					),

					casestudyTRowePrice: (
						<AppChrome>
							<CaseStudyTRowePrice />
						</AppChrome>
					),

					casestudyIntuit: (
						<AppChrome>
							<CaseStudyIntuit />
						</AppChrome>
					),

					expertiseAgileConsulting: (
						<AppChrome>
							<AgileConsulting />
						</AppChrome>
					),
					expertiseAngular: (
						<AppChrome>
							<AngularDevelopment />
						</AppChrome>
					),
					expertiseDojo: (
						<AppChrome>
							<DojoDevelopment />
						</AppChrome>
					),
					expertiseJavascript: (
						<AppChrome>
							<JavaScriptDevelopment />
						</AppChrome>
					),
					expertiseFullStack: (
						<AppChrome>
							<FullStackDevelopment />
						</AppChrome>
					),
					expertiseNode: (
						<AppChrome>
							<NodeDevelopment />
						</AppChrome>
					),
					expertiseReact: (
						<AppChrome>
							<ReactDevelopment />
						</AppChrome>
					),
					expertiseTypeScript: (
						<AppChrome>
							<TypeScriptDevelopment />
						</AppChrome>
					),
					expertiseVue: (
						<AppChrome>
							<VueDevelopment />
						</AppChrome>
					),
					servicesAutomatedTesting: (
						<AppChrome>
							<AutomatedTesting />
						</AppChrome>
					),
					servicesCrossPlatformDevelopment: (
						<AppChrome>
							<CrossPlatformDevelopment />
						</AppChrome>
					),
					servicesCustomSoftwareDevelopment: (
						<AppChrome>
							<CustomSoftwareDevelopment />
						</AppChrome>
					),
					servicesEnterpriseArchitecture: (
						<AppChrome>
							<EnterpriseArchitecture />
						</AppChrome>
					),
					servicesEnterpriseDevOpsSolutions: (
						<AppChrome>
							<DevOpsSolutions />
						</AppChrome>
					),
					servicesEnterpriseSoftwareDevelopment: (
						<AppChrome>
							<EnterpriseSoftwareDevelopment />
						</AppChrome>
					),
					servicesEnterpriseCloudServices: (
						<AppChrome>
							<EnterpriseCloudServices />
						</AppChrome>
					),
					servicesEnterpriseTechnologyStrategy: (
						<AppChrome>
							<EnterpriseTechnologyStrategy />
						</AppChrome>
					),
					servicesEnterpriseUX: (
						<AppChrome>
							<EnterpriseUX />
						</AppChrome>
					),
					servicesPerformanceOptimization: (
						<AppChrome>
							<PerformanceOptimization />
						</AppChrome>
					),
					servicesPerformanceTesting: (
						<AppChrome>
							<PerformanceTesting />
						</AppChrome>
					),
					servicesQualityAssurance: (
						<AppChrome>
							<QualityAssurance />
						</AppChrome>
					),
					servicesRapidPrototyping: (
						<AppChrome>
							<RapidPrototyping />
						</AppChrome>
					),
					servicesSoftwareArchitecture: (
						<AppChrome>
							<SoftwareArchitecture />
						</AppChrome>
					),
					servicesUIDesign: (
						<AppChrome>
							<UIDesign />
						</AppChrome>
					),
					servicesUXDesign: (
						<AppChrome>
							<UXDesign />
						</AppChrome>
					),
					servicesSoftwareModernization: (
						<AppChrome>
							<SoftwareModernization />
						</AppChrome>
					),
					servicesWebApplicationDevelopment: (
						<AppChrome>
							<WebApplicationDevelopment />
						</AppChrome>
					),
					casestudyCentric: (
						<AppChrome>
							<CaseStudyCentric />
						</AppChrome>
					),
					category: ({ params: { slug } }) => (
						<AppChrome>
							<Blogs type="category" termOrSlug={slug} />
						</AppChrome>
					),
					series: ({ params: { slug } }) => (
						<AppChrome>
							<Blogs type="series" termOrSlug={slug} />
						</AppChrome>
					),
					search: ({ queryParams: { term } }) => (
						<AppChrome>
							<Blogs type="search" termOrSlug={term} />
						</AppChrome>
					),
					blogPage: ({ params: { slugOrPage: page } }) => {
						return (
							<AppChrome>
								<Blogs page={parseInt(page)} />
							</AppChrome>
						);
					},
					blogPost: ({ params: { slugOrPage: slug } }) => {
						return (
							<AppChrome>
								<Blog slug={slug} />
							</AppChrome>
						);
					},
					blog: (
						<AppChrome>
							<Blogs page={1} />
						</AppChrome>
					),
					error404: (
						<AppChrome>
							<Error404 />
						</AppChrome>
					),
					previewSeriesLinks: <BlogSeries />
				}}
			</Outlet>
		</div>
	);
});

export default App;
